import React, { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { X, SwitchCamera, Camera } from "lucide-react";

const CameraCaptureWithPreview = ({ onCapture, onClose }) => {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [facingMode, setFacingMode] = useState("environment");
  const [error, setError] = useState(null);
  const [isSwitching, setIsSwitching] = useState(false);

  const stopStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  }, []);

  const startStream = useCallback(async (mode) => {
    setError(null);
    try {
      if (!navigator.mediaDevices?.getUserMedia) {
        throw new Error("Камера не поддерживается в этом браузере");
      }
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: mode,
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
      });
      streamRef.current = stream;
      if (videoRef.current) {
        const video = videoRef.current;
        video.srcObject = stream;
        await new Promise((resolve, reject) => {
          video.onloadedmetadata = () => {
            video.play().then(resolve).catch(reject);
          };
          video.onerror = reject;
        });
      }
    } catch (err) {
      if (err.name === "NotAllowedError" || err.name === "PermissionDeniedError") {
        setError("Доступ к камере запрещён. Разрешите доступ в настройках браузера");
      } else if (err.name === "NotFoundError" || err.name === "DevicesNotFoundError") {
        setError("Камера не найдена");
      } else if (err.name === "NotReadableError" || err.name === "TrackStartError") {
        setError("Камера уже используется другим приложением");
      } else if (err.name === "OverconstrainedError" || err.name === "ConstraintNotSatisfiedError") {
        setError("Камера не поддерживает требуемые параметры");
      } else {
        setError(err.message || "Ошибка при использовании камеры");
      }
    }
  }, []);

  useEffect(() => {
    startStream(facingMode);
    return () => {
      stopStream();
    };
  }, [facingMode, startStream, stopStream]);

  const handleSwitchCamera = async () => {
    setIsSwitching(true);
    stopStream();
    setFacingMode((prev) => (prev === "environment" ? "user" : "environment"));
    setIsSwitching(false);
  };

  const handleCapture = useCallback(() => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) return;
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0);
    canvas.toBlob(
      (blob) => {
        if (!blob) return;
        const file = new File(
          [blob],
          `camera-photo-${Date.now()}.jpg`,
          { type: "image/jpeg" }
        );
        stopStream();
        onCapture(file);
        onClose();
      },
      "image/jpeg",
      0.95
    );
  }, [onCapture, onClose, stopStream]);

  const handleClose = useCallback(() => {
    stopStream();
    onClose();
  }, [onClose, stopStream]);

  const overlay = (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 10001,
        backgroundColor: "#000",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "1rem",
          paddingTop: "max(1rem, env(safe-area-inset-top))",
          zIndex: 30,
        }}
      >
        <button
          type="button"
          onClick={handleClose}
          style={{
            padding: "0.5rem",
            background: "rgba(255,255,255,0.2)",
            border: "none",
            borderRadius: "9999px",
            color: "#fff",
            cursor: "pointer",
          }}
          aria-label="Закрыть"
        >
          <X size={24} />
        </button>
        <button
          type="button"
          onClick={handleSwitchCamera}
          disabled={isSwitching || !!error}
          style={{
            padding: "0.5rem",
            background: "rgba(255,255,255,0.2)",
            border: "none",
            borderRadius: "9999px",
            color: "#fff",
            cursor: isSwitching || error ? "not-allowed" : "pointer",
          }}
          aria-label="Переключить камеру"
        >
          <SwitchCamera size={24} />
        </button>
      </div>

      {error ? (
        <div
          style={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: "1rem",
            color: "#ef4444",
            textAlign: "center",
          }}
        >
          <p>{error}</p>
          <button
            type="button"
            onClick={handleClose}
            style={{
              marginTop: "1rem",
              padding: "0.5rem 1rem",
              background: "#ef4444",
              color: "#fff",
              border: "none",
              borderRadius: "0.5rem",
              cursor: "pointer",
            }}
          >
            Закрыть
          </button>
        </div>
      ) : (
        <>
          <div
            style={{
              flex: 1,
              minHeight: 0,
              position: "relative",
              overflow: "hidden",
            }}
          >
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              style={{
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
            />
          </div>
          <div
            style={{
              flexShrink: 0,
              position: "relative",
              zIndex: 20,
              padding: "1rem",
              paddingBottom: "max(1rem, env(safe-area-inset-bottom))",
              display: "flex",
              gap: "1rem",
              justifyContent: "center",
              flexWrap: "wrap",
              background: "rgba(0,0,0,0.5)",
            }}
          >
            <button
              type="button"
              onClick={handleClose}
              style={{
                padding: "0.75rem 1.5rem",
                background: "rgba(255,255,255,0.2)",
                color: "#fff",
                border: "none",
                borderRadius: "0.5rem",
                cursor: "pointer",
              }}
            >
              Отмена
            </button>
            <button
              type="button"
              onClick={handleCapture}
              style={{
                padding: "0.75rem 1.5rem",
                background: "#3b82f6",
                color: "#fff",
                border: "none",
                borderRadius: "0.5rem",
                cursor: "pointer",
              }}
            >
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "0.5rem",
                }}
              >
                <Camera size={20} />
                Сделать снимок
              </span>
            </button>
          </div>
        </>
      )}
    </div>
  );

  return createPortal(overlay, document.body);
};

export default CameraCaptureWithPreview;
